import React, { useState, useEffect, useCallback, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { roleAdminService } from 'api/services';
import type { PermissionCatalogDto, RolePermissionsDto, RoleSummaryDto } from 'api/types';
import { SettingsModalFrame, DetailList } from './SettingsModalFrame';

// ============================================================================
// ROLE MODAL (Settings → Roles & Permissions): one role in tabs
//   Details      code, name, description (and, when new, whose rights to start from)
//   Permissions  the screen × action matrix, grouped by sidebar section
//   Members      who holds the role (read-only; given under User Access)
// Modes: view (read-only, Edit switches to edit), edit, create, copy (a new role
// starting from another's rights). Minimizes to the bar with its unsaved state.
// The locked system role (FBP Super Administrator) only ever opens in view.
// ============================================================================

export type RoleModalMode = 'view' | 'edit' | 'create' | 'copy';
type RoleTab = 'details' | 'permissions' | 'members';

const ACTION_LABEL: Record<string, string> = {
  VIEW: 'View', CREATE: 'Create', UPDATE: 'Edit', DELETE: 'Delete', EXPORT: 'Export', IMPORT: 'Import',
  APPROVE: 'Approve', REJECT: 'Reject', PRINT: 'Print', MANAGE: 'Manage',
};

export const roleKindBadge = (r: Pick<RoleSummaryDto, 'isMutable' | 'isProductRole'>) => !r.isMutable
  ? <span className="badge" style={{ background: '#64748b', color: '#fff', fontSize: '0.7rem' }}>Locked</span>
  : r.isProductRole
    ? <span className="badge" style={{ background: '#2563eb', color: '#fff', fontSize: '0.7rem' }}>Product</span>
    : <span className="badge" style={{ background: '#059669', color: '#fff', fontSize: '0.7rem' }}>Custom</span>;

interface Form { code: string; name: string; description: string; copyFrom: string }
const EMPTY_FORM: Form = { code: '', name: '', description: '', copyFrom: '' };

type Screen = PermissionCatalogDto['modules'][number]['screens'][number];

// ---------------------------------------------------------------------------
// The matrix
// ---------------------------------------------------------------------------

const PermissionMatrix: React.FC<{
  catalog: PermissionCatalogDto; rights: Set<string>; original: Set<string>; editable: boolean; search: string;
  onChange: (next: Set<string>) => void;
}> = ({ catalog, rights, original, editable, search, onChange }) => {
  const viewOf = (s: Screen) => s.actions.find(a => a.actionCode === 'VIEW')?.resourceActionId;
  const apply = (fn: (next: Set<string>) => void) => { const next = new Set(rights); fn(next); onChange(next); };

  // Ticking any right ticks View (the screen must open); clearing View clears the screen.
  const tick = (next: Set<string>, s: Screen, actionCode: string, on: boolean) => {
    const id = s.actions.find(a => a.actionCode === actionCode)?.resourceActionId;
    if (!id) return;
    if (on) { next.add(id); const v = viewOf(s); if (v) next.add(v); }
    else if (actionCode === 'VIEW') s.actions.forEach(a => next.delete(a.resourceActionId));
    else next.delete(id);
  };

  const q = search.trim().toLowerCase();
  const cols = catalog.actions;

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
      <thead>
        <tr style={{ background: 'var(--grid-header-bg, #dbeafe)', position: 'sticky', top: 0, zIndex: 1 }}>
          <th style={{ textAlign: 'left', padding: '0.45rem 0.7rem' }}>Screen</th>
          {cols.map(a => <th key={a.code} style={{ padding: '0.45rem 0.4rem', width: 72, textAlign: 'center' }}>{ACTION_LABEL[a.code] ?? a.name}</th>)}
          <th style={{ width: 60, textAlign: 'center', padding: '0.45rem 0.4rem' }}>All</th>
        </tr>
      </thead>
      <tbody>
        {catalog.modules.map(m => {
          const screens = m.screens.filter(s => !q || s.screenName.toLowerCase().includes(q) || m.moduleName.toLowerCase().includes(q));
          if (screens.length === 0) return null;
          return (
            <React.Fragment key={m.moduleCode}>
              <tr style={{ background: 'var(--bg-secondary, #f8fafc)', borderTop: '1px solid var(--border-color)' }}>
                <td style={{ padding: '0.4rem 0.7rem', fontWeight: 700 }}>{m.moduleName}</td>
                {cols.map(a => {
                  const offering = screens.filter(s => s.actions.some(x => x.actionCode === a.code));
                  if (offering.length === 0) return <td key={a.code} />;
                  const all = offering.every(s => rights.has(s.actions.find(x => x.actionCode === a.code)!.resourceActionId));
                  return (
                    <td key={a.code} style={{ textAlign: 'center' }}>
                      <input type="checkbox" checked={all} disabled={!editable} title={`${ACTION_LABEL[a.code] ?? a.code} on every ${m.moduleName} screen`}
                        onChange={e => apply(next => offering.forEach(s => tick(next, s, a.code, e.target.checked)))} />
                    </td>
                  );
                })}
                <td />
              </tr>
              {screens.map(s => {
                const allOn = s.actions.every(a => rights.has(a.resourceActionId));
                return (
                  <tr key={s.screenCode} style={{ borderTop: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '0.35rem 0.7rem 0.35rem 1.5rem' }}>{s.screenName}</td>
                    {cols.map(a => {
                      const ra = s.actions.find(x => x.actionCode === a.code);
                      if (!ra) return <td key={a.code} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>·</td>;
                      const on = rights.has(ra.resourceActionId);
                      const changed = on !== original.has(ra.resourceActionId);
                      return (
                        <td key={a.code} style={{ textAlign: 'center', background: changed ? 'rgba(245,158,11,0.15)' : undefined }}>
                          <input type="checkbox" checked={on} disabled={!editable} onChange={e => apply(next => tick(next, s, a.code, e.target.checked))} />
                        </td>
                      );
                    })}
                    <td style={{ textAlign: 'center' }}>
                      <input type="checkbox" checked={allOn} disabled={!editable} title="Every right on this screen"
                        onChange={e => apply(next => s.actions.forEach(a => (e.target.checked ? next.add(a.resourceActionId) : next.delete(a.resourceActionId))))} />
                    </td>
                  </tr>
                );
              })}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
};

// ---------------------------------------------------------------------------
// The modal
// ---------------------------------------------------------------------------

export interface RoleModalProps {
  isOpen: boolean;
  mode: RoleModalMode;
  /** The role (view / edit) or the role to copy (copy). */
  roleId: string | null;
  roles: RoleSummaryDto[];
  catalog: PermissionCatalogDto | null;
  canEdit: boolean;
  initialTab?: RoleTab;
  onEdit: (roleId: string) => void;
  onSaved: () => void;
  onClose: () => void;
  tabId?: string;
  moduleName: string;
  createInstanceId?: string;
  minimizeRef?: React.MutableRefObject<(() => void) | null>;
  onRestoreModal?: (mode: RoleModalMode, roleId: string | null, instanceId?: string) => void;
}

const RoleModal: React.FC<RoleModalProps> = ({
  isOpen, mode, roleId, roles, catalog, canEdit, initialTab, onEdit, onSaved, onClose,
  tabId, moduleName, createInstanceId, minimizeRef, onRestoreModal,
}) => {
  const { minimize } = useModalMinimize();
  const [detail, setDetail] = useState<RolePermissionsDto | null>(null);
  const [form, setForm] = useState<Form>(EMPTY_FORM);
  const [rights, setRights] = useState<Set<string>>(new Set());
  const [original, setOriginal] = useState<Set<string>>(new Set());
  const [tab, setTab] = useState<RoleTab>('details');
  const [matrixSearch, setMatrixSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);

  const snapshotRef = useRef('');
  const isRestoreRef = useRef(false);
  const stateRef = useRef({ detail, form, rights, original, tab, mode, roleId, createInstanceId });
  stateRef.current = { detail, form, rights, original, tab, mode, roleId, createInstanceId };

  const snapshot = (f: Form, r: Set<string>) => JSON.stringify({ f, r: [...r].sort() });

  // Load on open (not on a restore, which brings its own state back).
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) { isRestoreRef.current = false; return; }
    setErrors({}); setMatrixSearch(''); setTab(initialTab ?? 'details');
    if (mode === 'create') {
      setDetail(null); setForm(EMPTY_FORM); setRights(new Set()); setOriginal(new Set());
      snapshotRef.current = snapshot(EMPTY_FORM, new Set());
      return;
    }
    if (!roleId) return;
    let cancelled = false;
    setDetail(null);
    setLoading(true);
    roleAdminService.getRole(roleId).then(d => {
      if (cancelled) return;
      const ids = new Set(d.resourceActionIds);
      if (mode === 'copy') {
        const f: Form = { code: '', name: `${d.role.name} (Copy)`, description: d.role.description ?? '', copyFrom: d.role.id };
        setDetail(null); setForm(f); setRights(ids); setOriginal(new Set());
        snapshotRef.current = snapshot({ ...f, name: '' }, new Set()); // a copy is unsaved from the start
      } else {
        const f: Form = { code: d.role.code, name: d.role.name, description: d.role.description ?? '', copyFrom: '' };
        setDetail(d); setForm(f); setRights(ids); setOriginal(ids);
        snapshotRef.current = snapshot(f, ids);
      }
    }).catch(err => {
      notificationService.toast.error(err?.response?.data?.message || 'Could not load the role.');
      onClose();
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mode, roleId]);

  const isNew = mode === 'create' || mode === 'copy';
  const locked = !!detail && !detail.role.isMutable;
  const editable = canEdit && !locked && mode !== 'view';
  const dirty = editable && snapshot(form, rights) !== snapshotRef.current;
  const added = [...rights].filter(x => !original.has(x)).length;
  const removed = [...original].filter(x => !rights.has(x)).length;

  const set = (patch: Partial<Form>) => { setForm(p => ({ ...p, ...patch })); setErrors({}); };

  /** A new role may start from another role's rights: load them into the matrix. */
  const pickCopyFrom = async (id: string) => {
    set({ copyFrom: id });
    if (!id) { setRights(new Set()); return; }
    try { setRights(new Set((await roleAdminService.getRole(id)).resourceActionIds)); }
    catch { notificationService.toast.error('Could not load that role\'s rights.'); }
  };

  const handleClose = useCallback(() => { if (dirty) setDiscardOpen(true); else onClose(); }, [dirty, onClose]);

  const handleMinimize = useCallback(() => {
    if (!tabId) return;
    const s = stateRef.current;
    const instance = s.createInstanceId || '1';
    const code = s.detail?.role.code ?? s.form.code;
    const modalId = s.mode === 'create' || s.mode === 'copy'
      ? `${tabId}-role-modal-${s.mode}-${instance}`
      : `${tabId}-role-modal-${s.mode}-${s.roleId}`;
    const title = s.mode === 'create' ? `New role${s.form.code ? ` - ${s.form.code}` : ''} (${instance})`
      : s.mode === 'copy' ? `Copy role${s.form.code ? ` - ${s.form.code}` : ''} (${instance})`
      : `${s.mode === 'edit' ? 'Edit' : 'View'} - ${code}`;
    const saved = { ...s, rights: new Set(s.rights), original: new Set(s.original), snap: snapshotRef.current };
    minimize({
      id: modalId, title, moduleName, tabId,
      isDirty: s.mode !== 'view' && snapshot(s.form, s.rights) !== snapshotRef.current,
      onRestore: () => {
        isRestoreRef.current = true;
        setDetail(saved.detail); setForm(saved.form); setRights(saved.rights); setOriginal(saved.original); setTab(saved.tab);
        snapshotRef.current = saved.snap;
        onRestoreModal?.(saved.mode, saved.roleId, isNewMode(saved.mode) ? instance : undefined);
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  const validate = () => {
    const e: Record<string, string> = {};
    if (isNew) {
      if (!form.code.trim()) e.code = 'Code is required.';
      else if (!/^[A-Z][A-Z0-9_]*$/.test(form.code)) e.code = 'Capital letters, digits and underscores, starting with a letter.';
      else if (form.code.startsWith('FBP_')) e.code = 'Codes starting with FBP_ are kept for the product\'s roles.';
    }
    if (!form.name.trim()) e.name = 'Name is required.';
    setErrors(e);
    if (Object.keys(e).length) setTab('details');
    return Object.keys(e).length === 0;
  };

  const handleConfirmSave = async () => {
    setSaveConfirmOpen(false);
    setSaving(true);
    try {
      if (isNew) {
        const created = await roleAdminService.createRole({ code: form.code.trim(), name: form.name.trim(), description: form.description.trim() || null, copyFromRoleId: null });
        if (rights.size > 0) await roleAdminService.saveRights(created.role.id, [...rights], created.role.rowVersion);
        notificationService.toast.success(`Role ${form.name.trim()} created with ${rights.size} rights.`);
      } else if (detail) {
        let rowVersion = detail.role.rowVersion;
        if (form.name.trim() !== detail.role.name || (form.description.trim() || null) !== (detail.role.description || null)) {
          rowVersion = (await roleAdminService.updateRole(detail.role.id, { name: form.name.trim(), description: form.description.trim() || null, rowVersion })).role.rowVersion;
        }
        if (added + removed > 0) {
          const res = await roleAdminService.saveRights(detail.role.id, [...rights], rowVersion);
          notificationService.toast.success(`Role saved: ${res.added} rights added, ${res.removed} removed. Signed-in users get them within a few seconds.`);
        } else {
          notificationService.toast.success('Role saved.');
        }
      }
      setSaving(false);
      onSaved();
      onClose();
    } catch (err: any) {
      setSaving(false);
      notificationService.toast.error(err?.response?.data?.message || 'Could not save the role.');
    }
  };

  if (!isOpen) return null;

  const fieldError = (k: string) => errors[k] && <div className="form-error-message">{errors[k]}</div>;
  const titleText = mode === 'create' ? 'New Role' : mode === 'copy' ? 'Copy Role'
    : `${mode === 'edit' ? 'Edit role' : 'Role'} — ${detail?.role.name ?? ''}`;

  return (
    <>
      <SettingsModalFrame<RoleTab>
        title={<span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>{titleText}{detail && roleKindBadge(detail.role)}</span>}
        subtitle={detail ? `${detail.role.code} · ${detail.members.length} user${detail.members.length === 1 ? '' : 's'} · ${rights.size} rights` : `${rights.size} rights selected`}
        onClose={handleClose}
        onMinimize={tabId ? handleMinimize : undefined}
        busy={saving || loading}
        busyLabel={loading ? 'Loading…' : 'Saving…'}
        tabs={[
          { id: 'details', label: 'Details' },
          { id: 'permissions', label: 'Permissions', count: rights.size },
          ...(isNew ? [] : [{ id: 'members' as RoleTab, label: 'Members', count: detail?.members.length ?? 0 }]),
        ]}
        activeTab={tab}
        onTabChange={setTab}
        footer={<>
          <span style={{ flex: 1, fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
            {locked ? 'Read-only: the locked system role keeps every right.'
              : mode === 'view' ? ''
              : !isNew && (added + removed > 0) ? `${added} rights to add, ${removed} to remove — not saved yet.`
              : 'Ticking any right also ticks View; clearing View clears the screen.'}
          </span>
          {mode === 'view' ? (
            <>
              {canEdit && !locked && detail && <button className="btn btn-secondary" onClick={() => onEdit(detail.role.id)}><IconSystem.edit size={14} /> Edit</button>}
              <button className="btn btn-primary" onClick={onClose}>Close</button>
            </>
          ) : (
            <>
              <button className="btn btn-secondary" onClick={handleClose} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={() => validate() && setSaveConfirmOpen(true)} disabled={saving || (!isNew && !dirty)}>Save</button>
            </>
          )}
        </>}
      >
        {tab === 'details' && (mode === 'view' || locked ? (
          detail && <DetailList rows={[
            ['Code', detail.role.code],
            ['Name', detail.role.name],
            ['Kind', roleKindBadge(detail.role)],
            ['Description', detail.role.description || (locked ? 'The locked system role: it always has every right, so someone can always reach every screen.' : '—')],
            ['Users holding it', detail.members.length],
            ['Rights', detail.resourceActionIds.length],
            ['Last changed', detail.role.updatedAt ? `${new Date(detail.role.updatedAt).toLocaleString()}${detail.role.updatedByName ? ` by ${detail.role.updatedByName}` : ''}` : '—'],
          ]} />
        ) : (
          <div style={{ display: 'grid', gap: '0.75rem', maxWidth: 720 }}>
            <div className="form-grid-2">
              <div className="form-group">
                <label className="form-label">Code {isNew && <span className="text-danger">*</span>}</label>
                <input className={`form-input${errors.code ? ' form-field-error' : ''}`} value={form.code} maxLength={50} disabled={!isNew}
                  placeholder="e.g. ACCOUNTS_CLERK" onChange={e => set({ code: e.target.value.toUpperCase().replace(/\s+/g, '_') })} />
                {fieldError('code')}
                {isNew && <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: '0.2rem' }}>Capital letters, digits and underscores. FBP_ is kept for the product's roles.</div>}
              </div>
              <div className="form-group">
                <label className="form-label">Name <span className="text-danger">*</span></label>
                <input className={`form-input${errors.name ? ' form-field-error' : ''}`} value={form.name} maxLength={200}
                  placeholder="e.g. Accounts Clerk" onChange={e => set({ name: e.target.value })} />
                {fieldError('name')}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea className="form-textarea" rows={3} value={form.description} maxLength={500} onChange={e => set({ description: e.target.value })} />
            </div>
            {mode === 'create' && (
              <div className="form-group">
                <label className="form-label">Start with the rights of</label>
                <select className="form-input" value={form.copyFrom} onChange={e => pickCopyFrom(e.target.value)}>
                  <option value="">No rights — tick them on the Permissions tab</option>
                  {roles.map(r => <option key={r.id} value={r.id}>{r.name} ({r.grantCount} rights)</option>)}
                </select>
              </div>
            )}
          </div>
        ))}

        {tab === 'permissions' && catalog && (
          <>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <div style={{ position: 'relative', width: 320 }}>
                <span style={{ position: 'absolute', left: 8, top: 7, color: 'var(--text-muted)' }}><IconSystem.search size={14} /></span>
                <input className="form-input" style={{ paddingLeft: 28, fontSize: '0.8125rem' }} value={matrixSearch}
                  placeholder="Find a screen or section…" onChange={e => setMatrixSearch(e.target.value)} />
              </div>
              {editable && !isNew && (added + removed > 0) && (
                <button className="btn btn-secondary" style={{ fontSize: '0.78rem', padding: '0.3rem 0.7rem' }} onClick={() => setRights(new Set(original))}>Undo rights changes</button>
              )}
            </div>
            <div style={{ border: '1px solid var(--border-color)', borderRadius: '0.5rem', overflow: 'auto', flex: 1, minHeight: 0 }}>
              <PermissionMatrix catalog={catalog} rights={rights} original={original} editable={editable} search={matrixSearch} onChange={setRights} />
            </div>
          </>
        )}

        {tab === 'members' && detail && (
          <>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>Roles are given to people under Settings → User Access.</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
              <thead><tr style={{ background: 'var(--grid-header-bg, #dbeafe)' }}>
                <th style={{ textAlign: 'left', padding: '0.45rem 0.7rem' }}>Name</th>
                <th style={{ textAlign: 'left', padding: '0.45rem 0.7rem' }}>User name</th>
                <th style={{ textAlign: 'left', padding: '0.45rem 0.7rem' }}>Email</th>
              </tr></thead>
              <tbody>
                {detail.members.length === 0 && <tr><td colSpan={3} style={{ padding: '0.6rem 0.7rem', color: 'var(--text-secondary)' }}>Nobody holds this role yet.</td></tr>}
                {detail.members.map(m => (
                  <tr key={m.userId} style={{ borderTop: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '0.4rem 0.7rem' }}>{m.fullName || '—'}</td>
                    <td style={{ padding: '0.4rem 0.7rem' }}>{m.userName || '—'}</td>
                    <td style={{ padding: '0.4rem 0.7rem' }}>{m.email || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </SettingsModalFrame>

      <ConfirmDialog open={saveConfirmOpen} title="Confirm Save"
        message={isNew ? `Create the role ${form.name.trim()} with ${rights.size} rights?`
          : `Save the role ${form.name.trim()}${added + removed > 0 ? ` (${added} rights added, ${removed} removed)` : ''}? Signed-in users get the change within a few seconds.`}
        confirmLabel="Confirm" cancelLabel="Cancel" variant="default"
        onConfirm={handleConfirmSave} onCancel={() => setSaveConfirmOpen(false)} />
      <ConfirmDialog open={discardOpen} title="Discard Changes?" message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard" cancelLabel="Keep Editing" variant="danger"
        onConfirm={() => { setDiscardOpen(false); onClose(); }} onCancel={() => setDiscardOpen(false)} />
    </>
  );
};

const isNewMode = (m: RoleModalMode) => m === 'create' || m === 'copy';

export default RoleModal;
